// Cache utility for localStorage with expiration

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

const CACHE_PREFIX = 'hockey_api_cache_';

export function getCachedData<T>(key: string): T | null {
  try {
    const cacheKey = CACHE_PREFIX + key;
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) return null;
    
    const entry: CacheEntry<T> = JSON.parse(cached);
    
    // Check if expired
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return entry.data;
  } catch (error) {
    console.warn('Cache read error:', error);
    return null;
  }
}

export function setCachedData<T>(key: string, data: T, ttlMinutes: number = 5): void {
  try {
    const cacheKey = CACHE_PREFIX + key;
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttlMinutes * 60 * 1000,
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(entry));
  } catch (error) {
    // Handle quota exceeded or other storage errors
    console.warn('Cache write error:', error);
    // Try to clear old cache entries
    clearExpiredCache();
  }
}

export function clearCache(keyPattern?: string): void {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        if (!keyPattern || key.includes(keyPattern)) {
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Cache clear error:', error);
  }
}

export function clearExpiredCache(): void {
  try {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const entry = JSON.parse(cached);
            if (Date.now() > entry.expiresAt) {
              keysToRemove.push(key);
            }
          }
        } catch {
          // Invalid entry, remove it
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Expired cache clear error:', error);
  }
}

// Cache TTL presets (in minutes)
export const CACHE_TTL = {
  LIVE_GAMES: 1,      // 1 minute for live data
  STANDINGS: 10,      // 10 minutes for standings
  PLAYER_STATS: 15,   // 15 minutes for player stats
  STATIC_DATA: 60,    // 1 hour for rarely changing data
} as const;

// Generate cache key from query params
export function generateCacheKey(prefix: string, params: Record<string, unknown>): string {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('_');
  return `${prefix}_${sortedParams}`;
}
